import {Component, OnInit} from '@angular/core';
import {OrganizationService} from "../shared/service/organization.service";
import {Router} from "@angular/router";
import {take} from "rxjs/operators";
import { OidcService } from "../core/oidc/oidc.service";
import {DialogComponent} from "../dialog/dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {ProcessingService} from "../processing-overlay/processing-service";
import {AppConfig} from "../shared/constants/app.config";

@Component({
  selector: 'app-organization.gate',
  imports: [],
  template: '',
  styleUrl: './organization.gate.component.scss',
})
export class OrganizationGateComponent implements OnInit {

  constructor(
    private readonly organizationService: OrganizationService,
    private readonly oidcService: OidcService,
    private readonly router: Router,
    readonly dialog: MatDialog,
    private readonly processingService: ProcessingService
  ) {}

  ngOnInit(): void {
    console.log('ORG CHECK STARTED');
    this.oidcService.getAccessToken()
      .pipe(take(1))
      .subscribe(token => {
        if (token) {
          this.processOidcToken(token);
        } else {
          // in case user is not logged in yet
          console.error('AuthGuard passed but no token found. Forcing login.');
          this.oidcService.login();
        }
    });
  }

  private processOidcToken(accessToken: string): void {
    try {
      const decodedValue = this.oidcService.decodeToken(accessToken);
      this.processOrganization(decodedValue, accessToken);
    } catch (error) {
      console.error('Error decoding OIDC token:', error);
    }
  }

  private async processOrganization( decodedValue: any, accessToken: string ): Promise<void> {
    try {
      if (!AppConfig.ENABLE_MSPI_ORG_LOOKUP && this.isMsyncOnlyUser(decodedValue)) {
        await this.router.navigate(['/landing'], {
          state: {
            oktaToken: accessToken,
            decoded: decodedValue
          }
        });
        return;
      }

      this.processingService.show('Checking Organization...');
      const organizations = await this.determineOrganizations(decodedValue);
      this.processingService.hide();

      if (!organizations?.length) {
        throw new Error('No Organization found for the authenticated user.');
      }

      const cibmtrCenters = this.organizationsToCenters(organizations);
      // if multiple orgs for login - select org and then do eula for that
      if (cibmtrCenters.length > 1) {

        const dialogRef =
          this.dialog.open(DialogComponent, {
            data: {cibmtrCenters},
            disableClose: true,
          });

        dialogRef.afterClosed().subscribe(
          async (selectedItem) => {
            if (!selectedItem) {
              return;
            }
            if(AppConfig.ENABLE_EULA) {
              await this.processEula(selectedItem, accessToken, decodedValue);
            } else {
              this.goToLanding(accessToken, decodedValue, selectedItem);
            }
          }
        );

      } else {
        // Only one Organization — use cibmtrCenters[0]
        if(AppConfig.ENABLE_EULA) {
          await this.processEula(cibmtrCenters[0], accessToken, decodedValue)
        } else {
          this.goToLanding(accessToken, decodedValue, cibmtrCenters[0]);
        }
      }
    } catch (error) {
      this.processingService.hide();
      console.error('Error processing Organization:', error);
      throw error;
    }
  }

  private isMsyncOnlyUser(decodedValue: any): boolean {
    const msync = decodedValue?.authz_msync_fhir_ehr_client;
    const cibmtr = decodedValue?.authz_cibmtr_fhir_ehr_client;

    return !!msync && !cibmtr;
  }

  private organizationsToCenters(organizations: any[]): any[] {
    return organizations.map(
      (organization: any) => {
        const telecom = organization.telecom || [];

        const telecomUrlItems =
          telecom.filter(
            (item: any) =>
              item.system === 'url'
          );

        return {
          value: 'rc_' + organization.identifier?.[0]?.value,
          name: organization.name,
          selected: false,
          telecomUrlItems,
          organization
        };
      }
    );
  }

  // process eula for selected org
  async processEula(selectedItem: any, accessToken: string, decodedValue: any) {
    try
    {
      await this.showProcessingMessage('Checking EULA Status...');
      const organization = selectedItem.organization;

      // Get eula status
      const eulaStatus = this.getEulaStatus(organization);

      // if eula already accepted
      if (eulaStatus && eulaStatus.accepted === true) {
        await this.showProcessingMessage('EULA already accepted. Loading application...');
        this.goToLanding(accessToken, decodedValue, selectedItem);
        setTimeout(() => {
          this.processingService.hide();
        }, 250);
        return;
      }

      // if eula never shown
      if (!eulaStatus) {
        const initializedOrg = this.initializeEula(organization);
        const updatedSelectedOrg = {...selectedItem, organization: initializedOrg };
        await this.showProcessingMessage('EULA not yet accepted. Loading EULA...');
        this.goToEula( accessToken, decodedValue, updatedSelectedOrg, 30,false);
        return;
      }

      // Eula present, but not accepted yet
      const daysRemaining = this.calculateDaysRemaining(eulaStatus.firstPresentedDate);
      await this.showProcessingMessage('EULA not yet accepted. Loading EULA...');
      this.goToEula(accessToken, decodedValue, selectedItem, daysRemaining, true);

    } catch (error) {
      this.processingService.hide();
      console.error('Error during Organization/EULA initialization:', error);
      throw error;
    }
  }

  private goToEula( accessToken: string, decodedValue: any, selectedOrg: any, bypassDaysRemaining: number = 30, eulaAlreadyExists: boolean = true ): void {
    this.router.navigate(['/eula'], {
      state: {
        oktaToken: accessToken,
        decoded: decodedValue,
        selectedOrg,
        bypassDaysRemaining,
        eulaAlreadyExists
      }
    });
  }

  private goToLanding( accessToken: string, decodedValue: any, selectedOrg: any): void {
    this.router.navigate(['/landing'], {
      state: {
        oktaToken: accessToken,
        decoded: decodedValue,
        selectedOrg
      }
    });
  }

  private getEulaStatus(organization: any): any | null {
    const eulaExtension =
      organization.extension?.find(
        (ext: any) =>
          ext.url ===
          'https://cibmtr.org/fhir/StructureDefinition/cra-eula-status'
      );

    if (!eulaExtension) {
      return null;
    }

    const getValue = (url: string): any => {
      const extension =
        eulaExtension.extension?.find(
          (ext: any) => ext.url === url
        );

      return extension?.valueDateTime
        ?? extension?.valueBoolean
        ?? extension?.valueString;
    };

    return {
      firstPresentedDate: getValue('firstPresentedDate'),
      accepted: getValue('accepted'),
      eulaVersion: getValue('eulaVersion'),
      acceptedDate: getValue('acceptedDate')
    };
  }

  private initializeEula(organization: any): any {
    const updatedOrganization = structuredClone(organization);

    if (!updatedOrganization.extension) {
      updatedOrganization.extension = [];
    }

    updatedOrganization.extension.push({
      url: 'https://cibmtr.org/fhir/StructureDefinition/cra-eula-status',
      extension: [
        {
          url: 'firstPresentedDate',
          valueDateTime: new Date().toISOString()
        },
        {
          url: 'accepted',
          valueBoolean: false
        },
        {
          url: 'eulaVersion',
          valueString: '1.0'
        }
      ]
    });

    return updatedOrganization;
  }

  private calculateDaysRemaining( firstPresentedDate: string ): number {
    const firstDate = new Date(firstPresentedDate);

    const firstCalendarDate = new Date(
      firstDate.getFullYear(),
      firstDate.getMonth(),
      firstDate.getDate()
    );

    const now = new Date();

    const today = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    const millisecondsPerDay = 1000 * 60 * 60 * 24;

    const daysPassed = Math.floor(
      (today.getTime() - firstCalendarDate.getTime()) /
      millisecondsPerDay
    );

    return Math.max(0, 30 - daysPassed);
  }

  private async determineOrganizations( decodedValue: any ): Promise<any[]> {
    let scopes: string[] = [];

    const craScopes =
      decodedValue.authz_cibmtr_fhir_ehr_client?.filter(
        (item: string) =>
          item.includes('_role_rc') &&
          item.includes('_fn3')
      ) || [];

    if (craScopes.length > 0) {
      scopes = craScopes
        .map((scope: string) => {
          const match = scope.match(/(rc_\d+)_fn3/);
          return match ? match[1] : null;
        })
        .filter(Boolean);
    } else { //mspi scopes only
      const msyncScopes = decodedValue.authz_msync_fhir_ehr_tc_role || [];

      scopes = msyncScopes
        .map((scope: string) => {
          const match = scope.match(/(tc_\d+)/);
          return match ? match[1] : null;
        })
        .filter((tc: string | null) => tc !== 'tc_500')
        .filter(Boolean) as string[];
    }

    return this.organizationService.fetchRawOrganizations(
      scopes.join(',')
    );
  }

  private async showProcessingMessage(message: string): Promise<void> {
    this.processingService.show(message);
    await new Promise<void>(
      resolve => setTimeout(resolve, 450)
    );
  }
}
